/**
 * AI Preferences Toggle Component
 *
 * Allows users to switch between floating button and sidebar modes.
 * Can be placed in settings or as a quick toggle in the AI interface.
 */

import { cn } from "@plasma/utils/cn";
import { MessageSquare, PanelRight } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { logger, PortalEvents } from "~/lib/logger";

import { type AIMode, useAIPreferences } from "./hooks/use-ai-preferences";

interface AIPreferencesToggleProps {
  /** Optional class name */
  className?: string;
  /** Compact mode (icon only) */
  compact?: boolean;
}

export function AIPreferencesToggle({
  className,
  compact = false,
}: AIPreferencesToggleProps) {
  const { preferences, setPreferredMode, isLoading } = useAIPreferences();

  const handleToggle = async (mode: AIMode) => {
    if (mode !== preferences.preferredMode) {
      logger.info(PortalEvents.AI_MODE_TOGGLED, {
        from_mode: preferences.preferredMode,
        to_mode: mode,
      });
      await setPreferredMode(mode);
    }
  };

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={
                preferences.preferredMode === "floating"
                  ? "Switch to sidebar mode"
                  : "Switch to floating mode"
              }
              className={cn("h-8 w-8", className)}
              disabled={isLoading}
              onClick={() =>
                handleToggle(
                  preferences.preferredMode === "floating"
                    ? "sidebar"
                    : "floating"
                )
              }
              size="icon"
              type="button"
              variant="ghost"
            >
              {preferences.preferredMode === "sidebar" ? (
                <PanelRight className="h-4 w-4" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              Switch to{" "}
              {preferences.preferredMode === "floating"
                ? "sidebar"
                : "floating"}{" "}
              mode
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      className={cn(
        "bg-muted flex items-center gap-1 rounded-lg p-1",
        className
      )}
    >
      <Button
        className="h-8 gap-2"
        disabled={isLoading}
        onClick={() => handleToggle("floating")}
        size="sm"
        type="button"
        variant={preferences.preferredMode === "floating" ? "default" : "ghost"}
      >
        <MessageSquare className="h-4 w-4" />
        Floating
      </Button>
      <Button
        className="h-8 gap-2"
        disabled={isLoading}
        onClick={() => handleToggle("sidebar")}
        size="sm"
        type="button"
        variant={preferences.preferredMode === "sidebar" ? "default" : "ghost"}
      >
        <PanelRight className="h-4 w-4" />
        Sidebar
      </Button>
    </div>
  );
}
